import React, { useState } from "react";
import * as Mui from "@mui/material";
import { CloudDownload, Description, DownloadForOffline } from "@mui/icons-material";
import * as XLSX from "xlsx"; 
import { BulkSalaryImportData } from "./Bulk_salary_details"; 
import { useSelector } from "react-redux";
import { showToast } from "services/utils/Status";
import { Loader } from "components/Loader";
import { BackArrow } from "components/CustomComponents/BackArrow";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { themes } from "services/constants"; 

export const BulkSalaryImportPage = () => {
  const history = useHistory();
  const { domain, token } = useSelector((state) => state.tokenReducer);
  const [importedData, setImportData] = useState([]);
  const [bulkData, setBulkData] = useState([]);
  const [fileName, setFileName] = useState("");
  const [fileSize, setFileSize] = useState("");
  const [page, setPage] = useState(0);
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [showListView, setShowListView] = useState(false);
  const [loading, setLoading] = useState(false);

  // Added "Company" to header
  const header = ["Emp ID", "Emp Name", "Company", "Template Name", "Salary", "Salary Type", "Effective From", "Payroll Group"];

  const downloadSampleFile = () => {
    const sampleData = [
      { 
        "Employee ID": "874569", 
        "Employee Name": "Organflow", 
        "Company": "Your Company Name", // Added Company
        "Template Name": "Default template", 
        "Salary": 50000,
        "Salary Type": "Monthly",
        "Effective From": "01-04-2024",
        "Payroll Group": "Default Payroll Group"
      }
    ];
    const ws = XLSX.utils.json_to_sheet(sampleData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Salary_Template");
    XLSX.writeFile(wb, "Sample Salary Details.xlsx");
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setFileName(file.name);
    setFileSize((file.size / 1024).toFixed(2) + " KB");
    setLoading(true);

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const bstr = event.target.result;
        const wb = XLSX.read(bstr, { type: "binary" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rawData = XLSX.utils.sheet_to_json(ws, { raw: false, defval: "" });

        const formattedPayload = rawData.map((row) => {
          const normalizeKey = (key) => key.replace(/\s+/g, "").toLowerCase();
          const getVal = (possibleKeys) => {
            const matchedKey = Object.keys(row).find((k) =>
              possibleKeys.includes(normalizeKey(k))
            );
            return matchedKey ? row[matchedKey] : "";
          };

          return {
            employee_id:    getVal(["empid", "employeeid"]),
            user_name:      getVal(["empname", "employeename", "name"]),
            company:        getVal(["company", "companyname", "organization"]), // Extract Company
            template_name:  getVal(["templatename", "template"]),
            salary:         getVal(["salary", "grosssalary"]),
            salary_type:    getVal(["salarytype"]),
            effective_from: getVal(["effectivefrom", "date"]),
            payroll_group:  getVal(["payrollgroup", "group"]),
          };
        });

        const response = await fetch(`${domain}bulk-salary-import/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `token ${token}`,
          },
          body: JSON.stringify(formattedPayload),
        });

        const res = await response.json();

        if (response.ok) {
          const dataWithStatus = res.map((row, index) => ({
            ...formattedPayload[index],
            ...row,
            status: row.is_valid ? "Valid" : "Invalid",
          }));

          setImportData(dataWithStatus);
          setBulkData(dataWithStatus);
          const hasErrors = dataWithStatus.some((row) => row.is_valid === false);
          setButtonDisabled(hasErrors);
          setShowListView(true);
        } else {
          const errorMsg = res.errors ? res.errors.join(" | ") : (res.error || "Data validation failed.");
          showToast("error", errorMsg);
        }
      } catch (error) {
        console.error('[BulkSalaryImport] operation failed:', error);
        showToast("error", "Error reading or processing the file.");
      } finally {
        setLoading(false);
      }
    };
    reader.readAsBinaryString(file);
    e.target.value = null;
  };

  const clearImportedData = () => {
    setImportData([]);
    setBulkData([]);
    setFileName("");
    setFileSize("");
    setShowListView(false);
  };

  return (
    <React.Fragment>
      {!loading && !showListView ? (
        <Mui.Grid container sx={{ paddingTop: 3 }}>
          <Mui.Grid xs={2.5}>
            <BackArrow actionFunction={() => { history.push("/admin/hrzone/employees"); }} />
          </Mui.Grid>
          <Mui.Grid xs={7} sx={{ paddingTop: "8%", flexDirection: "column", justifyContent: "center" }}>
            <Mui.Box component={Mui.Paper} elevation={3}>
              <Mui.Typography sx={{ fontWeight: "bold", fontSize: 22, padding: 2 }}>
                Bulk Salary Import
              </Mui.Typography>
              <Mui.Grid container sx={{ paddingLeft: 3, paddingRight: 3, paddingTop: 2 }}>
                <Mui.Grid container sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", padding: 5, border: `1px solid ${themes.blueColor}`, borderStyle: "dashed" }}>
                  <Mui.Stack>
                    <Mui.Grid container sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
                      <CloudDownload sx={{ color: themes.blueColor }} fontSize="large" />
                    </Mui.Grid>
                    <Mui.Typography sx={{ textAlign: "center", fontSize: 20 }}>
                      Browse your file(s){" "}
                      <Mui.Button component="label">
                        <Mui.Typography sx={{ fontSize: 17, textTransform: "capitalize" }}>Import</Mui.Typography>
                        <input accept=".xlsx, .xls, .csv" style={{ display: "none" }} type="file" onChange={handleFileUpload} />
                      </Mui.Button>{" "}
                    </Mui.Typography>
                    <Mui.Typography sx={{ fontSize: 20, color: themes.colorShadow }}>
                      Max 10 MB files are allowed
                    </Mui.Typography>
                  </Mui.Stack>
                </Mui.Grid>
                <Mui.Grid container sx={{ paddingTop: 3 }}>
                  <Mui.Typography sx={{ fontSize: 15, color: themes.colorShadow }}>
                    Only support .xlsx, .xls, .csv file
                  </Mui.Typography>
                </Mui.Grid>
              </Mui.Grid>
              <Mui.Divider sx={{ paddingLeft: 3, paddingRight: 3 }}>Sample File</Mui.Divider>
              <Mui.Grid container sx={{ paddingLeft: 3, paddingRight: 3, paddingBottom: 2 }}>
                <Mui.Grid container sx={{ borderRadius: 2, border: `0.5px solid ${themes.shadowText}`, borderColor: themes.shadowText }}>
                  <Mui.Grid xs={1.5} sx={{ paddingTop: 1.5, paddingLeft: 1 }}>
                    <Description fontSize="large" sx={{ color: themes.primaryIndicator, fontSize: 45 }} />
                  </Mui.Grid>
                  <Mui.Grid xs={3.5} sx={{ paddingTop: 1 }}>
                    <Mui.Stack>
                      <Mui.Typography>Sample Salary Details.xlsx</Mui.Typography>
                      <Mui.Typography>288 B</Mui.Typography>
                    </Mui.Stack>
                  </Mui.Grid>
                  <Mui.Grid xs={7} sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-end", alignItems: "flex-end" }}>
                    <Mui.IconButton onClick={() => downloadSampleFile()}>
                      <DownloadForOffline size="large" sx={{ color: themes.downloadIconColor, fontSize: 50 }} />
                    </Mui.IconButton>
                  </Mui.Grid>
                </Mui.Grid>
              </Mui.Grid>
            </Mui.Box>
          </Mui.Grid>
          <Mui.Grid xs={2.5}></Mui.Grid>
        </Mui.Grid>
      ) : loading ? (
        <Loader info="Reading and Validating File..." />
      ) : null}
      {showListView ? (
        <Mui.Grid container sx={{ paddingTop: 3 }}>
          <BulkSalaryImportData
            header={header} importedData={importedData} setImportData={setImportData} page={page} setPage={setPage}
            fileName={fileName} clearImportedData={clearImportedData} fileSize={fileSize} setBulkData={setBulkData}
            bulkData={bulkData} buttonDisabled={buttonDisabled} setButtonDisabled={setButtonDisabled} setShowListView={setShowListView}
          />
        </Mui.Grid>
      ) : null}
    </React.Fragment>
  );
};